import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Play,
    BookOpen,
    Video,
    Music,
    ExternalLink,
    Search,
    Filter,
    Loader2,
} from 'lucide-react';

interface ContentItem {
    id: string;
    title: string;
    description: string | null;
    content_type: 'video' | 'article' | 'podcast';
    url: string;
    thumbnail_url: string | null;
    category: string | null;
    created_at: string;
}

export function AthleteContentPage() {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);

    // Get pairing to find tenant
    const { data: pairing } = useQuery({
        queryKey: ['athlete-pairing', user?.id],
        queryFn: async () => {
            const { data } = await supabase
                .from('student_pairings')
                .select('tenant_id')
                .eq('student_id', user?.id)
                .eq('status', 'active')
                .single();
            return data;
        },
        enabled: !!user?.id,
    });

    // Fetch curated content
    const { data: content, isLoading } = useQuery({
        queryKey: ['athlete-content', pairing?.tenant_id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('curated_content')
                .select('*')
                .eq('tenant_id', pairing?.tenant_id)
                .eq('is_active', true)
                .order('sort_order', { ascending: true });

            if (error) throw error;
            return data as ContentItem[];
        },
        enabled: !!pairing?.tenant_id,
    });

    const categories = [...new Set(content?.map(c => c.category).filter(Boolean))];

    const filteredContent = content?.filter(item => {
        const matchesSearch = !searchTerm ||
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !activeCategory || item.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    const getIcon = (type: string) => {
        switch (type) {
            case 'video': return <Video className="w-5 h-5" />;
            case 'podcast': return <Music className="w-5 h-5" />;
            default: return <BookOpen className="w-5 h-5" />;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="section-title flex items-center gap-2">
                    <BookOpen className="w-6 h-6" />
                    Biblioteca de Conteúdo
                </h1>
                <p className="section-subtitle">
                    Conteúdos recomendados pelo seu Personal
                </p>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar conteúdo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                />
            </div>

            {/* Categories */}
            {categories.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                    <button
                        onClick={() => setActiveCategory(null)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === null
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                    >
                        Todos
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === cat
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                                }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            )}

            {/* Content Grid */}
            {filteredContent && filteredContent.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                    {filteredContent.map((item) => (
                        <a
                            key={item.id}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="card-hover group"
                        >
                            <div className="flex gap-4">
                                {item.thumbnail_url ? (
                                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                        <img
                                            src={item.thumbnail_url}
                                            alt={item.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 flex items-center justify-center flex-shrink-0">
                                        {getIcon(item.content_type)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary-600 transition-colors">
                                            {item.title}
                                        </h3>
                                        <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    {item.description && (
                                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                                            {item.description}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className={`badge ${item.content_type === 'video' ? 'badge-primary' :
                                                item.content_type === 'podcast' ? 'badge-secondary' : 'badge-outline'
                                            }`}>
                                            {item.content_type === 'video' ? 'Vídeo' :
                                                item.content_type === 'podcast' ? 'Podcast' : 'Artigo'}
                                        </span>
                                        {item.category && (
                                            <span className="text-xs text-gray-400">{item.category}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            ) : (
                <div className="card text-center py-12">
                    <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                        Nenhum conteúdo disponível
                    </h3>
                    <p className="text-gray-500">
                        Seu Personal ainda não adicionou conteúdos para você.
                    </p>
                </div>
            )}
        </div>
    );
}
